/**
 * ============================================================
 * System       : CSAF Alert Finance
 * Objective    : Automated Reserve Fund Notification System
 * File         : frontend/src/api.js
 * Description  : Axios instance, interceptors, all API calls
 * ------------------------------------------------------------
 * Developer    : Chokchai Thainkhao (Head of IT)
 * Co-Author    : AI Assisted (Claude) — โครงสร้างและฟังก์ชันหลัก
 * Modified &
 *  Integrated  : Chokchai Thainkhao
 * Version      : 1.0.0
 * Date         : May 2026
 * Copyright    : © 2026 Chokchai T. All rights reserved.
 * ============================================================
 */

import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('csaf_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Redirect to login on 401; propagate all other errors
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('csaf_token');
      localStorage.removeItem('csaf_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ---- Auth ----
export const login = (email, password) => api.post('/auth/login', { email, password });
export const getMe  = () => api.get('/auth/me');

// ---- Records ----
export const getRecords = (cleared = 'all') =>
  api.get('/records', { params: { cleared } });

export const importFile = (formData) =>
  api.post('/records/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const sendAlert = (id, n) =>
  api.patch(`/records/${id}/alert/${n}`);

export const sendAnnualAlert = (id, n) =>
  api.patch(`/records/${id}/annual-alert/${n}`);

export const updateRecordType = (id, reserve_type) =>
  api.patch(`/records/${id}/type`, { reserve_type });

export const clearRecord = (id, amount) =>
  api.patch(`/records/${id}/clear`, amount != null ? { amount } : {});

export const adjustClearRecord = (id, cleared_amount) =>
  api.patch(`/records/${id}/clear-adjust`, { cleared_amount });

export const deleteRecord = (id) =>
  api.delete(`/records/${id}`);

export const updateRecordNote = (id, note) =>
  api.patch(`/records/${id}/note`, { note });

// ---- Users ----
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const setUserPassword   = (id, password)   => api.put(`/users/${id}/password`, { password });
export const addUserCompany    = (id, companyId)  => api.post(`/users/${id}/companies`, { company_id: companyId });
export const removeUserCompany = (id, companyId)  => api.delete(`/users/${id}/companies/${companyId}`);

// ---- Companies ----
export const getCompanies    = ()           => api.get('/companies');
export const createCompany   = (name)       => api.post('/companies', { name });
export const updateCompany   = (id, name)   => api.put(`/companies/${id}`, { name });
export const deleteCompany   = (id)         => api.delete(`/companies/${id}`);

// ---- History / Notifications ----
export const getHistory = (type = 'all', days = '30') =>
  api.get('/history', { params: { type, days } });
export const clearHistory = () => api.delete('/history');
export const getNotifications = () => api.get('/notifications');

// ---- Settings ----
export const getSettings = () => api.get('/settings');
export const saveSettings = (data) => api.put('/settings', data);
export const testSmtp = () => api.post('/settings/test-smtp');
export const testLine = () => api.post('/settings/test-line');
export const testLineGrouped = () => api.post('/settings/test-line-grouped');

// ---- Reports ----
export const getReportExcel = () =>
  api.get('/reports/excel', { responseType: 'blob' });
export const getReportPdf = () =>
  api.get('/reports/pdf', { responseType: 'blob' });
export const getReportCsv = () =>
  api.get('/reports/csv', { responseType: 'blob' });

// ---- Reconciliation (SAP vs Bank) ----
export const getReconciliations = (period) =>
  api.get('/reconciliation', { params: period ? { period } : {} });
export const getReconciliation = (id) => api.get(`/reconciliation/${id}`);
export const saveReconciliation = (data) => api.post('/reconciliation', data);
// Used by ReconciliationHistory.jsx's editable detail view — updates just
// the parts a manual match on a saved report can change (counts + payload),
// not the whole run (period/filenames/original totals stay as first saved).
export const updateReconciliation = (id, data) => api.put(`/reconciliation/${id}`, data);
export const deleteReconciliation = (id) => api.delete(`/reconciliation/${id}`);

export default api;
